import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import PageHeader from '@/components/system/PageHeader';
import SectionCard from '@/components/system/SectionCard';
import MetricTooltip, { METRIC_EXPLANATIONS } from '@/components/system/MetricTooltip';
import { CHART_TOOLTIP_STYLE } from '@/components/system/ChartTooltip';
import { InteractiveChart } from '@/components/system/InteractiveChart';
import { InsightList } from '@/components/system/InsightCard';
import { DiagnosticPanel } from '@/components/system/DiagnosticPanel';
import { RecommendationPanel } from '@/components/system/RecommendationPanel';
import { generateInsights } from '@/lib/insightRules';
import { generateDiagnostic } from '@/lib/diagnosticEngine';
import { generateRecommendations } from '@/lib/recommendationEngine';
import type { AnalyticalInsight } from '@/types/insights';
import type { DiagnosticContext } from '@/types/diagnostics';
import type { RootCauseAnalysis } from '@/types/diagnostics';
import type { OperationalRecommendation } from '@/types/recommendations';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  BarChart3,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Building2,
  ShieldAlert,
  Activity,
  ArrowRight,
  Play,
  Loader2,
  MapPin,
  Lightbulb,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

// ─── Types ──────────────────────────────────────────────────────────
interface ContractRanking {
  contract_id: string;
  contrato_nome: string;
  contratada: string;
  cluster_nome: string;
  score_criticidade: number;
  nivel_criticidade: string;
  prob_atraso_mc: number | null;
  prob_estouro_custo_mc: number | null;
  score_risco: number | null;
  tendencia: string | null;
  total_ocorrencias: number | null;
  ultima_simulacao: string | null;
}

interface RiskAnomaly {
  contract_id: string;
  contrato_nome: string;
  tipo_anomalia: string;
  descricao: string;
  severidade: string;
  detalhes: any;
  detectado_em: string;
}

// Risk tokens — centralized from design system
import { riskColor as riskColorToken, RISK_LABELS, riskLevelFromScore } from '@/lib/riskTokens';
const CRITICIDADE_COLORS: Record<string, string> = {
  critico: riskColorToken('critico'),
  alto: riskColorToken('alto'),
  moderado: riskColorToken('moderado'),
  baixo: riskColorToken('baixo'),
};

const CRITICIDADE_LABELS: Record<string, string> = {
  critico: 'Crítico',
  alto: 'Alto',
  moderado: 'Moderado',
  baixo: 'Baixo',
};

const ANOMALY_LABELS: Record<string, string> = {
  mc_outlier_historico: 'Resultado fora do padrão histórico',
  mc_outlier_global: 'Contrato fora da média global',
  indicador_salto_abrupto: 'Variação abrupta de indicador',
};

function TendenciaIcon({ tendencia }: { tendencia: string | null }) {
  if (tendencia === 'piorando') return <TrendingUp className="h-4 w-4 text-destructive" />;
  if (tendencia === 'melhorando') return <TrendingDown className="h-4 w-4 text-success" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

function ScoreBar({ score }: { score: number }) {
  const nivel = score >= 80 ? 'critico' : score >= 60 ? 'alto' : score >= 40 ? 'moderado' : 'baixo';
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(score, 100)}%`, backgroundColor: CRITICIDADE_COLORS[nivel] }}
        />
      </div>
      <span className="text-xs font-mono font-medium tabular-nums w-8 text-right">{score}</span>
    </div>
  );
}

// KpiCard replaced by ClickableKpiCard from Design System
import { ClickableKpiCard } from '@/components/system/ClickableKpiCard';

// Active filter for distribution drill-down
type CriticalityFilter = string | null;

// ─── Main Component ─────────────────────────────────────────────────
const RiskIntelligenceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [ranking, setRanking] = useState<ContractRanking[]>([]);
  const [anomalies, setAnomalies] = useState<RiskAnomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [critFilter, setCritFilter] = useState<CriticalityFilter>(null);
  const [activeDiagnostic, setActiveDiagnostic] = useState<RootCauseAnalysis | null>(null);
  const [activeRecommendations, setActiveRecommendations] = useState<OperationalRecommendation[]>([]);
  const [activeInsight, setActiveInsight] = useState<AnalyticalInsight | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [rankRes, anomRes] = await Promise.all([
      supabase.from('vw_risk_contract_ranking').select('*').order('score_criticidade', { ascending: false }),
      supabase.from('vw_risk_anomalies').select('*').order('detectado_em', { ascending: false }).limit(5),
    ]);
    setRanking((rankRes.data || []) as ContractRanking[]);
    setAnomalies((anomRes.data || []) as RiskAnomaly[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBootstrap = useCallback(async () => {
    setBootstrapping(true);
    try {
      const { data, error } = await supabase.functions.invoke('risk-monte-carlo', {
        body: { mode: 'bootstrap', n_simulations: 5000 },
      });
      if (error) throw error;
      toast({
        title: 'Simulações concluídas',
        description: `${data.simulated} contratos simulados com sucesso.${data.errors?.length ? ` ${data.errors.length} erros.` : ''}`,
      });
      // Reload data to reflect new scores
      await loadData();
    } catch (err: any) {
      console.error('Bootstrap error:', err);
      toast({
        variant: 'destructive',
        title: 'Erro ao executar simulações',
        description: err.message || 'Erro inesperado',
      });
    } finally {
      setBootstrapping(false);
    }
  }, [toast, loadData]);

  // ── Computed KPIs
  const kpis = useMemo(() => {
    const total = ranking.length;
    const criticos = ranking.filter(r => r.nivel_criticidade === 'critico').length;
    const altos = ranking.filter(r => r.nivel_criticidade === 'alto').length;
    const piorando = ranking.filter(r => r.tendencia === 'piorando').length;

    // Portfolio trend
    let tendenciaCarteira: 'Melhorando' | 'Estável' | 'Piorando' = 'Estável';
    if (piorando > total * 0.3) tendenciaCarteira = 'Piorando';
    else if (piorando === 0 && total > 0) tendenciaCarteira = 'Melhorando';

    return { total, criticos, altos, piorando, anomalias: anomalies.length, tendenciaCarteira };
  }, [ranking, anomalies]);

  // ── Distribution chart data
  const distributionData = useMemo(() => {
    const counts: Record<string, number> = { critico: 0, alto: 0, moderado: 0, baixo: 0 };
    ranking.forEach(r => { counts[r.nivel_criticidade] = (counts[r.nivel_criticidade] || 0) + 1; });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([key, value]) => ({ name: CRITICIDADE_LABELS[key], value, fill: CRITICIDADE_COLORS[key], key }));
  }, [ranking]);

  // Filtered ranking for drill-down from pie chart
  const filteredRanking = useMemo(() => {
    if (!critFilter) return ranking;
    return ranking.filter(r => r.nivel_criticidade === critFilter);
  }, [ranking, critFilter]);

  const handlePieClick = useCallback((_: any, index: number) => {
    const entry = distributionData[index];
    if (!entry) return;
    setCritFilter(prev => prev === entry.key ? null : entry.key);
  }, [distributionData]);

  // ── Diagnostic context (shared between insights and diagnostics)
  const diagnosticContext: DiagnosticContext = useMemo(() => ({
    contracts: ranking.map(r => ({
      contract_id: r.contract_id,
      contrato_nome: r.contrato_nome,
      score_criticidade: r.score_criticidade,
      nivel_criticidade: r.nivel_criticidade,
      prob_atraso_mc: r.prob_atraso_mc,
      tendencia: r.tendencia,
      total_ocorrencias: r.total_ocorrencias,
      cluster_nome: r.cluster_nome,
    })),
    portfolio: {
      total: kpis.total,
      criticos: kpis.criticos,
      altos: kpis.altos,
      moderados: ranking.filter(r => r.nivel_criticidade === 'moderado').length,
      baixos: ranking.filter(r => r.nivel_criticidade === 'baixo').length,
      piorando: kpis.piorando,
    },
  }), [ranking, kpis]);

  // ── Insights from rule engine
  const insights = useMemo(() => generateInsights({
    contracts: diagnosticContext.contracts,
    portfolio: diagnosticContext.portfolio,
  }), [diagnosticContext]);

  // ── Handle insight click → generate diagnostic
  const handleInsightClick = useCallback((insight: AnalyticalInsight) => {
    const diag = generateDiagnostic(insight, diagnosticContext);
    setActiveDiagnostic(diag);
    setActiveInsight(insight);
    if (diag) {
      const recs = generateRecommendations(insight, diag, diagnosticContext);
      setActiveRecommendations(recs);
    } else {
      setActiveRecommendations([]);
    }
  }, [diagnosticContext]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Inteligência de Risco" subtitle="Carregando painel analítico..." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 rounded-[10px]" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-[400px] rounded-[10px] lg:col-span-2" />
          <Skeleton className="h-[400px] rounded-[10px]" />
        </div>
      </div>
    );
  }

  const allScoresZero = ranking.length > 0 && ranking.every(r => r.score_criticidade === 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PageHeader
          title="Inteligência de Risco"
          subtitle="Visão consolidada de risco e criticidade da carteira de contratos"
        />
        {allScoresZero && (
          <Button
            onClick={handleBootstrap}
            disabled={bootstrapping}
            className="gap-2"
            size="sm"
          >
            {bootstrapping ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {bootstrapping ? 'Simulando...' : 'Executar Simulações'}
          </Button>
        )}
      </div>

      {/* Bootstrap notice */}
      {allScoresZero && !bootstrapping && (
        <div className="rounded-[10px] border border-border bg-muted/30 p-4 flex items-start gap-3">
          <Activity className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Nenhuma simulação foi executada ainda</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Clique em "Executar Simulações" para gerar os primeiros scores de risco Monte Carlo para todos os contratos ativos.
            </p>
          </div>
        </div>
      )}

      {/* ── KPI Row ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <ClickableKpiCard label="Contratos Ativos" value={kpis.total} icon={Building2} onClick={() => navigate('/empreendimentos/contratos')} actionHint="Ver contratos" />
        <ClickableKpiCard label="Críticos" value={kpis.criticos} icon={ShieldAlert} variant={kpis.criticos > 0 ? 'destructive' : 'default'} tooltip={METRIC_EXPLANATIONS.scoreCriticidade} onClick={() => navigate('/empreendimentos/risk-analytics')} actionHint="Ver análise de risco" />
        <ClickableKpiCard label="Risco Alto" value={kpis.altos} icon={AlertTriangle} variant={kpis.altos > 0 ? 'warning' : 'default'} tooltip="Contratos com score de criticidade entre 60 e 79 pontos." onClick={() => navigate('/empreendimentos/inteligencia/uo')} actionHint="Ver risco por UO" />
        <ClickableKpiCard label="Em Deterioração" value={kpis.piorando} icon={TrendingUp} variant={kpis.piorando > 0 ? 'destructive' : 'default'} tooltip="Contratos cuja probabilidade de atraso aumentou na última simulação." onClick={() => navigate('/empreendimentos/inteligencia/tendencias')} actionHint="Ver tendências" />
        <ClickableKpiCard label="Risco por UO" value="Mapa" icon={MapPin} onClick={() => navigate('/empreendimentos/inteligencia/uo')} actionHint="Ver mapa de risco" tooltip="Análise geográfica de risco por Unidade Operacional (cidade)." />
        <ClickableKpiCard
          label="Tendência da Carteira"
          value={kpis.tendenciaCarteira}
          icon={Activity}
          variant={kpis.tendenciaCarteira === 'Piorando' ? 'destructive' : kpis.tendenciaCarteira === 'Melhorando' ? 'success' : 'default'}
          tooltip="Avaliação geral: 'Piorando' se >30% dos contratos estão em deterioração."
        />
      </div>

      {/* ── Main Content ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Ranking */}
        <SectionCard
          title={critFilter ? `Ranking — ${CRITICIDADE_LABELS[critFilter]}` : 'Ranking de Contratos'}
          icon={BarChart3}
          className="lg:col-span-2"
        >
          {filteredRanking.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              {critFilter ? (
                <>
                  <p className="text-sm">Nenhum contrato com criticidade "{CRITICIDADE_LABELS[critFilter]}"</p>
                  <button onClick={() => setCritFilter(null)} className="text-xs text-primary mt-2 hover:underline">Limpar filtro</button>
                </>
              ) : (
                <>
                  <Building2 className="h-10 w-10 mb-3 opacity-40" />
                  <p className="text-sm">Nenhuma simulação concluída</p>
                  <p className="text-xs mt-1">Execute simulações Monte Carlo para gerar o ranking</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {critFilter && (
                <button onClick={() => setCritFilter(null)} className="text-xs text-primary hover:underline mb-1">
                  ✕ Limpar filtro
                </button>
              )}
              {filteredRanking.map((contract) => (
                <button
                  key={contract.contract_id}
                  onClick={() => navigate(`/empreendimentos/inteligencia/${contract.contract_id}`)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-left group"
                >
                  <Badge
                    variant={contract.nivel_criticidade === 'critico' ? 'destructive' : 'outline'}
                    className="text-xs min-w-[62px] justify-center"
                    style={
                      contract.nivel_criticidade !== 'critico'
                        ? { borderColor: CRITICIDADE_COLORS[contract.nivel_criticidade], color: CRITICIDADE_COLORS[contract.nivel_criticidade] }
                        : undefined
                    }
                  >
                    {CRITICIDADE_LABELS[contract.nivel_criticidade]}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{contract.contrato_nome}</span>
                      {anomalies.some(a => a.contract_id === contract.contract_id) && (
                        <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning,30_90%_50%))] flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{contract.contratada} · {contract.cluster_nome}</p>
                  </div>
                  <TendenciaIcon tendencia={contract.tendencia} />
                  <div className="w-28 hidden sm:block">
                    <ScoreBar score={contract.score_criticidade} />
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground opacity-40 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Insights — prominent position */}
          {insights.length > 0 && (
            <SectionCard title="Insights do Sistema" icon={Lightbulb}>
              <InsightList insights={insights} maxItems={4} compact onInsightClick={handleInsightClick} />
            </SectionCard>
          )}

          {/* Anomalies */}
          <SectionCard title="Anomalias Detectadas" icon={AlertTriangle}>
            {anomalies.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma anomalia ativa</p>
            ) : (
              <div className="space-y-2">
                {anomalies.map((anomaly, idx) => (
                  <button
                    key={`${anomaly.contract_id}-${anomaly.tipo_anomalia}-${idx}`}
                    onClick={() => navigate(`/empreendimentos/inteligencia/${anomaly.contract_id}`)}
                    className="w-full text-left p-3 rounded-lg border hover:bg-muted/30 transition-colors"
                    style={{ borderColor: anomaly.severidade === 'critico' ? 'hsl(var(--destructive) / 0.3)' : undefined }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge
                        variant={anomaly.severidade === 'critico' ? 'destructive' : 'outline'}
                        className="text-xs"
                      >
                        {anomaly.severidade}
                      </Badge>
                      <span className="text-xs font-medium truncate">{anomaly.contrato_nome}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {ANOMALY_LABELS[anomaly.tipo_anomalia] || anomaly.descricao}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Distribution — Interactive */}
          <SectionCard title="Distribuição de Risco" icon={BarChart3}>
            <InteractiveChart
              data={distributionData}
              hint="Clique em uma fatia para filtrar o ranking"
              emptyMessage="Sem dados para exibir"
              activeFilter={critFilter}
              onClearFilter={() => setCritFilter(null)}
              height={180}
            >
              {(chartProps) => (
                <PieChart>
                  <Pie
                    data={distributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={70}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                    onClick={handlePieClick}
                    className="cursor-pointer"
                  >
                    {distributionData.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={entry.fill}
                        {...chartProps.getCellProps(entry.key)}
                      />
                    ))}
                  </Pie>
                  <Tooltip {...chartProps.tooltipProps} />
                </PieChart>
              )}
            </InteractiveChart>
            <div className="flex flex-wrap justify-center gap-3 -mt-2">
              {distributionData.map((d, i) => (
                <button
                  key={i}
                  className={`flex items-center gap-1.5 hover:opacity-80 transition-opacity ${critFilter && critFilter !== d.key ? 'opacity-40' : ''}`}
                  onClick={() => setCritFilter(prev => prev === d.key ? null : d.key)}
                >
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
                  <span className="text-xs text-muted-foreground">{d.name}: {d.value}</span>
                </button>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>

      {/* ── Diagnóstico de Causa Raiz ────────────────────── */}
      {activeDiagnostic && (
        <DiagnosticPanel
          diagnostic={activeDiagnostic}
          onClose={() => { setActiveDiagnostic(null); setActiveRecommendations([]); setActiveInsight(null); }}
        />
      )}

      {/* ── Recomendações Operacionais ────────────────────── */}
      {activeRecommendations.length > 0 && (
        <RecommendationPanel
          recommendations={activeRecommendations}
          onClose={() => setActiveRecommendations([])}
        />
      )}
    </div>
  );
};

export default RiskIntelligenceDashboard;
